/**
 * `weight_int8` beside `weight_scale` — the third spelling of int8-with-scale.
 *
 * ## What it is, and why it needs a folder rather than a rule
 *
 * `EschaLabs/Qwen3.6-35B-A3B-Escha-W2` stores its linear-attention projections,
 * shared experts, embedding and head as an `I8` payload with an `F16` per-row
 * scale:
 *
 *     ….in_proj_qkv.weight_int8   I8   [8192, 2048]
 *     ….in_proj_qkv.weight_scale  F16  [8192]
 *
 * **The parameter count is already right** and that is what makes this subtle.
 * One byte is one weight and the payload carries the logical shape, so nothing
 * here is misreported the way `eschamoe`'s `I16` codes or Kimi's `U8`
 * `weight_packed` were. The defect is narrower: the two tensors do not group,
 * so the scale plane is accounted as a separate logical tensor of its own and
 * the payload's cost is stated without it.
 *
 * That understates the scheme. An `I8` payload alone reads exactly 8.00 bits per
 * weight — the number a reader would get by knowing the dtype and doing no work
 * — while the honest figure includes the scale that makes the payload
 * interpretable. It also puts a row in the map for a plane that is not a
 * component of the model.
 *
 * ## Why `baseName` cannot do it
 *
 * The suffixes are joined with an **underscore**, not a dot, so the generic
 * `PART_SUFFIXES` strip does not reach them — the same reason MXFP4's
 * `_blocks`/`_scales` needed `block-scaled.ts`. And an underscore suffix cannot
 * be stripped blindly: `_scale` would swallow any tensor whose name merely ends
 * that way and fold it into a prefix that may hold nothing else.
 *
 * So membership is a **verified pair or nothing**, on the same terms as every
 * other folder here: both tensors present, the payload `I8`, and the scale's
 * leading dimension matching the payload's. A lone `weight_scale` — which this
 * repo also ships, beside `escha_*` groups — is left entirely alone.
 */

import type { InventoryTensor } from "../safetensors/inventory.js";

const PAYLOAD = ".weight_int8";
const SCALE = ".weight_scale";

export interface Int8ScaledGroup {
  /** Logical name — the module path, e.g. `….in_proj_qkv`. */
  readonly prefix: string;
  /** The payload tensor: `weight_int8`. */
  readonly payloadName: string;
  /** Both physical tensors, payload first. */
  readonly partNames: readonly string[];
  /** Weights in the logical matrix. One byte is one weight, so: the payload's. */
  readonly params: number;
  /** Payload plus scale. */
  readonly bytes: number;
  /** What the scheme actually costs, the scale included. */
  readonly effectiveBpw: number;
  /** The payload alone. Exactly 8, by construction. */
  readonly payloadBpw: number;
}

/**
 * Every verified `weight_int8`/`weight_scale` pair.
 *
 * Takes no `declared`, deliberately. This repo's config announces `eschamoe`,
 * which is true of its routed experts and says nothing about these tensors —
 * the same split `e8m0-scaled.ts` documents, where a repo's declared scheme
 * describes one part of the model and is silent or wrong about another.
 */
export function findInt8ScaledGroups(
  tensors: readonly InventoryTensor[],
): Int8ScaledGroup[] {
  const payloads = new Map<string, InventoryTensor>();
  const scales = new Map<string, InventoryTensor>();
  for (const tensor of tensors) {
    if (tensor.name.endsWith(PAYLOAD)) {
      payloads.set(tensor.name.slice(0, -PAYLOAD.length), tensor);
    } else if (tensor.name.endsWith(SCALE)) {
      scales.set(tensor.name.slice(0, -SCALE.length), tensor);
    }
  }

  const groups: Int8ScaledGroup[] = [];
  for (const [prefix, payload] of payloads) {
    const scale = scales.get(prefix);
    if (!scale) continue;
    /*
     * The dtype is the check that matters. `I8` is what makes "one byte is one
     * weight" true, and a payload of any other width under this name would mean
     * the reader has misunderstood the scheme — in which case folding would
     * assert a parameter count nobody verified.
     */
    if (payload.dtype !== "I8") continue;
    // Per-row scale: one entry per output row. A scale that does not line up
    // with the payload's leading dimension is not the plane this thinks it is.
    if (scale.shape[0] !== payload.shape[0]) continue;
    if (payload.numel <= 0) continue;

    const bytes = payload.byteLength + scale.byteLength;
    groups.push({
      prefix,
      payloadName: payload.name,
      partNames: [payload.name, scale.name],
      params: payload.numel,
      bytes,
      effectiveBpw: (bytes * 8) / payload.numel,
      payloadBpw: (payload.byteLength * 8) / payload.numel,
    });
  }
  return groups;
}
